'use client'

import { useEffect } from 'react'

const allowedOrigins = [
  'https://form.jotform.com',
  'https://www.jotform.com',
  'https://submit.jotform.com',
]

const iframeProps = ({ formId }: { formId: string }) => ({
  allowFullScreen: true,
  src: `https://form.jotform.com/${formId}`,
  frameBorder: '0',
  style: {
    minWidth: '100%',
    height: '100vh',
    border: 'none',
  },
  scrolling: 'no',
  className: 'jotformIframeContainer',
  id: `JotFormIFrame-${formId}`,
})

function hasMozCancelFullScreen(
  doc: Document,
): doc is Document & { mozCancelFullScreen: () => Promise<void> } {
  return 'mozCancelFullScreen' in doc
}

function hasWebkitExitFullscreen(
  doc: Document,
): doc is Document & { webkitExitFullscreen: () => Promise<void> } {
  return 'webkitExitFullscreen' in doc
}

function hasMsExitFullscreen(
  doc: Document,
): doc is Document & { msExitFullscreen: () => Promise<void> } {
  return 'msExitFullscreen' in doc
}

const JotFormEmbed = ({ formId }: { formId: string }) => {
  useEffect(() => {
    const ifr = document.getElementById(
      `JotFormIFrame-${formId}`,
    ) as HTMLIFrameElement | null

    if (ifr) {
      let src = ifr.src
      let iframeParams: string[] = []

      if (window.location.href.includes('?')) {
        iframeParams = iframeParams.concat(
          window.location.href
            .substring(window.location.href.indexOf('?') + 1)
            .split('&'),
        )
      }

      if (src.includes('?')) {
        iframeParams = iframeParams.concat(
          src.substring(src.indexOf('?') + 1).split('&'),
        )
        src = src.substring(0, src.indexOf('?'))
      }

      iframeParams.push('isIframeEmbed=1')
      ifr.src = `${src}?${iframeParams.join('&')}`
    }

    const handleIFrameMessage = (e: MessageEvent) => {
      if (!allowedOrigins.includes(e.origin)) return
      if (typeof e.data !== 'string') return

      const args = e.data.split(':')

      const iframe =
        args.length > 2
          ? document.getElementById(`JotFormIFrame-${args[args.length - 1]}`)
          : document.getElementById(`JotFormIFrame-${formId}`)

      if (!iframe) return

      switch (args[0]) {
        case 'scrollIntoView':
          iframe.scrollIntoView()
          break

        case 'setHeight':
          ;(iframe as HTMLIFrameElement).style.height = `${args[1]}px`
          break

        case 'collapseErrorPage':
          if ((iframe as HTMLIFrameElement).clientHeight > window.innerHeight) {
            ;(iframe as HTMLIFrameElement).style.height = `${window.innerHeight}px`
          }
          break

        case 'reloadPage':
          window.location.reload()
          break

        case 'loadScript': {
          let src = args[1]

          if (args.length > 3) {
            src = `${args[1]}:${args[2]}`
          }

          const script = document.createElement('script')
          script.src = src
          script.type = 'text/javascript'
          document.body.appendChild(script)
          break
        }

        case 'exitFullscreen':
          if (document.exitFullscreen) {
            document.exitFullscreen()
          } else if (hasMozCancelFullScreen(document)) {
            document.mozCancelFullScreen()
          } else if (hasWebkitExitFullscreen(document)) {
            document.webkitExitFullscreen()
          } else if (hasMsExitFullscreen(document)) {
            document.msExitFullscreen()
          }
          break
      }
    }

    window.addEventListener('message', handleIFrameMessage)

    return () => {
      window.removeEventListener('message', handleIFrameMessage)
    }
  }, [formId])

  return (
      <iframe {...iframeProps({ formId })}></iframe>
  )
}

export default JotFormEmbed